import React from 'react'
import {
   ThaiMeaningFullProps,
   ThaiExComWord,
   ThaiWordBasicProps,
} from 'core/interfaces/dictionary-thai-interfaces'
import InlineExplanation from './explanation/inline-explanation'
import ExpandedExplanation from './explanation/expanded-explanation'
import ThaiWordMeaningText from './thai-word-meaning-text'
import PartOfSpeechChips from '../part-of-speech-chips'
import WordBreakdownPanel from './word-breakdown-panel'
import ExampleSentencesList from '../example-sentences-list'
import ExampleSentencesHeader from '../example-sentences-header'
import { SentenceWithTranslit } from '../../../lib/interfaces'
import { getExplanationType } from '../../../lib/translation-explanations'
import { useTranslit } from 'hooks/useTranslit'

type Props = {
   meaning: ThaiMeaningFullProps
   // Linked senses of the same group (oil ↔ oily), rendered as compact
   // sub-lines under the primary meaning. The worker has already pooled their
   // compounds and sentences onto the primary.
   siblingMeanings?: ThaiMeaningFullProps[]
   index: number
   // Sentences classified to this sense.
   exampleSentences: SentenceWithTranslit[]
   // The page's headword, for the coral pick-out inside example sentences.
   headword: string
   // Prefix the meaning with its "1." index. Suppressed for monosemous words,
   // where a single "1." reads as noise.
   showNumber: boolean
   // Drop the compound's parts from the related grid when they're already shown
   // in the word-breakdown panel below.
   hideComponents: boolean
   // The compound's constituent parts (>= 2). Passed only for the sense that
   // owns the breakdown (the first), which renders it under the meaning.
   breakdownParts?: ThaiExComWord[]
}

const allEtymology = [
   'English',
   'Chinese',
   'Greek',
   'French',
   'Indonesian',
   'Italian',
   'Japanese',
   'Malaysian',
]

// Usage/etymology qualifiers shown as a muted note beside the POS chips (the
// part of speech itself is rendered as coloured chips, see PartOfSpeechChips).
const getPosNotes = (usage: string, etymology: string): string[] => {
   const notes: string[] = []

   if (usage === 'Formal') {
      notes.push('formal, deferential')
   } else if (usage === 'Colloquial') {
      notes.push('colloquial')
   } else if (usage === 'Polite') {
      notes.push('polite, elegant')
   } else if (usage === 'Offensive') {
      notes.push('impolite & potentially offensive')
   }

   if (etymology.length > 0) {
      if (allEtymology.includes(etymology)) {
         notes.push(`transliteration from ${etymology}`)
      } else if (etymology === 'Pali/Sanskrit') {
         notes.push('of Pali/Sanskrit origin')
      } else if (etymology === 'Khmer') {
         notes.push('of Khmer origin')
      }
   }

   return notes
}

// A heteronym sense states its own reading next to the part-of-speech chips:
// สระ's "vowel" sense is สะ-หฺระ where the headword is สะ. Renders nothing for
// the ordinary sense that just reads like its headword. The respelling shows
// even when the reader has romanization switched off, matching the hero.
const SensePronunciation = ({ phoneticOverride }: { phoneticOverride?: string | null }) => {
   const translit = useTranslit().of(phoneticOverride ?? '')
   const phonetic = phoneticOverride?.trim()
   if (!phonetic) return null

   return (
      <span className="text-[13px] italic text-textMuted">
         pronounced{' '}
         {translit && (
            <>
               <span className="font-translit font-semibold text-translit">{translit}</span>{' '}
            </>
         )}
         <span className="font-thai not-italic thai">[ {phonetic} ]</span>
      </span>
   )
}

const convertExComToThaiWordBasic = (exComWords: ThaiExComWord[]): ThaiWordBasicProps[] => {
   const words = exComWords.map((ec) => {
      return {
         word: ec.word,
         t2e: ec.t2e,
         commonnessScore: ec.commonnessScore,
         hasSoundFile: ec.hasSoundFile,
         audioGenders: ec.audioGenders,
         meanings: [
            {
               word: ec.word,
               usage: ec.usage,
               displayOrder: ec.displayOrder,
               isUncommon: false,
               id: ec.id,
               meaning: ec.meaning,
               partOfSpeech: ec.partOfSpeech,
               explanation: ec.explanation,
            },
         ],
      }
   })
   return words
}

const ThaiWordMeanings = (props: Props) => {
   const { meaning, index } = props
   const explanationType = getExplanationType(meaning.explanation, meaning.meaning)
   const components = convertExComToThaiWordBasic(meaning.components)
   const examples = convertExComToThaiWordBasic(meaning.examples)
   const relatedWords = props.hideComponents ? examples : [...components, ...examples]
   const posNotes = getPosNotes(meaning.usage, meaning.etymology)
   const breakdownWords = props.breakdownParts
      ? convertExComToThaiWordBasic(props.breakdownParts)
      : []

   // The breakdown panel renders only with parts to break down or related words
   // (see WordBreakdownPanel); mirror that here so this component owns the
   // layout. When both the panel and the sense's sentences exist they sit side
   // by side as a two-column reading view.
   const hasBreakdown = breakdownWords.length >= 2 || relatedWords.length > 0
   const hasSentences = props.exampleSentences.length > 0
   const twoColumn = hasBreakdown && hasSentences

   return (
      <li
         id={`sense-${index + 1}`}
         className="list-none bg-transparent border-0 rounded-none shadow-none pt-[6px] pb-[10px] m-0 scroll-mt-[90px] [&~li]:border-t [&~li]:border-t-hairline [&~li]:mt-[32px] [&~li]:pt-[32px]"
      >
         <div className="flex flex-wrap items-center gap-[8px] mb-[8px]">
            <PartOfSpeechChips value={meaning.partOfSpeech} />
            {posNotes.length > 0 && (
               <span className="text-[13px] italic text-textMuted">{posNotes.join(', ')}</span>
            )}
            <SensePronunciation phoneticOverride={meaning.phoneticOverride} />
         </div>
         <div className="text-[26px] font-extrabold tracking-[-0.01em] text-bodyBlack leading-[1.2]">
            {props.showNumber && (
               <span className="text-textMuted font-extrabold">{index + 1}.</span>
            )}{' '}
            <ThaiWordMeaningText
               meaning={meaning}
               engTranslationWords={meaning.engTranslationWords}
            />
         </div>
         <InlineExplanation explanation={meaning.explanation} explanationType={explanationType} />
         <ExpandedExplanation explanation={meaning.explanation} explanationType={explanationType} />

         {(props.siblingMeanings ?? []).map((sibling, siblingIndex) => {
            const siblingPosNotes = getPosNotes(sibling.usage, sibling.etymology)
            return (
               <div
                  key={siblingIndex}
                  className="mt-[14px] pl-[14px] border-l-[3px] border-l-hairline"
               >
                  <div className="flex flex-wrap items-center gap-[8px] mb-[4px]">
                     <PartOfSpeechChips value={sibling.partOfSpeech} />
                     {siblingPosNotes.length > 0 && (
                        <span className="text-[13px] italic text-textMuted">
                           {siblingPosNotes.join(', ')}
                        </span>
                     )}
                     <SensePronunciation phoneticOverride={sibling.phoneticOverride} />
                  </div>
                  <div className="text-[20px] font-extrabold tracking-[-0.01em] text-bodyBlack leading-[1.25]">
                     <ThaiWordMeaningText
                        meaning={sibling}
                        engTranslationWords={sibling.engTranslationWords}
                     />
                  </div>
               </div>
            )
         })}

         {(hasBreakdown || hasSentences) && (
            <div
               className={`mt-[40px] ${twoColumn ? 'flex max-[860px]:block items-start gap-0' : ''}`}
            >
               {hasBreakdown && (
                  <div className={twoColumn ? 'flex-1 min-w-0 pr-[44px] max-[860px]:pr-0' : ''}>
                     <WordBreakdownPanel
                        parts={breakdownWords}
                        relatedWords={relatedWords}
                        headword={props.headword}
                        wide={!hasSentences}
                     />
                  </div>
               )}
               {hasSentences && (
                  <div
                     className={
                        twoColumn
                           ? 'flex-1 min-w-0 pl-[44px] border-l border-l-hairline max-[860px]:mt-[24px] max-[860px]:pt-[24px] max-[860px]:pl-0 max-[860px]:border-l-0 max-[860px]:border-t max-[860px]:border-t-hairline'
                           : ''
                     }
                  >
                     <ExampleSentencesHeader />
                     <ExampleSentencesList
                        exampleSentences={props.exampleSentences}
                        headword={props.headword}
                        embedded
                     />
                  </div>
               )}
            </div>
         )}
      </li>
   )
}

export default ThaiWordMeanings
